import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { Trans, useTranslation } from 'react-i18next';
import { useAppDispatch, useAppSelector } from '@/app/hooks';
import { useRestaurant } from '@/features/restaurant/useRestaurant';
import {
  useGetAvailabilityQuery,
  useCreateReservationMutation,
} from './reservationApi';
import {
  selectReservation,
  setPartySize,
  setDate,
  setTime,
  setCustomer,
  setNote,
  resetReservation,
} from './reservationSlice';
import { selectIsAuthenticated, selectCurrentUser } from '@/features/user/selectors';
import { showErrorToast } from '@/features/ui/uiSlice';
import { pad, buildReservedFor } from './dateUtils';
import { retryAfterSeconds, formatRetryDelay } from './retryHint';
import { groupSlots, slotForHour } from './slotPicker';
import { localParts, restaurantTz } from '@/lib/hours';
import type { Closure, WeeklyHours } from '@/types/restaurant';
import { DAY_KEYS, normalizeReservationMaxPartySize } from '@/types/restaurant';
import TurnstileWidget, { type TurnstileWidgetHandle } from '@/features/user/AccountModal/TurnstileWidget';
import { isChannelPaused } from '@/features/restaurant/pausedChannels';

const ADVANCE_DAYS = 14;
const WEEKDAYS_FR = ['Dim', 'Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam'];
// Mirrors backend RESERVATION_MIN_LEAD_MINUTES (reservations/service.py).
const RESERVATION_MIN_LEAD_MS = 10 * 60 * 1000;

// SC-1081 — les jours proposes partent du jour civil du RESTAURANT (`tz`), pas
// de celui du navigateur : a 22:30 a Cayenne, Paris est deja le lendemain.
// L'arithmetique se fait ensuite sur des dates nues (UTC), sans fuseau.
export function buildDateChips(advanceDays: number, now: Date, tz: string) {
  const p = localParts(now, tz);
  const chips: { date: string; topLabel: string; bottomLabel: string }[] = [];
  for (let i = 0; i < advanceDays; i += 1) {
    const d = new Date(Date.UTC(p.year, p.month - 1, p.day + i));
    chips.push({
      date: `${d.getUTCFullYear()}-${pad(d.getUTCMonth() + 1)}-${pad(d.getUTCDate())}`,
      topLabel: WEEKDAYS_FR[d.getUTCDay()],
      bottomLabel: pad(d.getUTCDate()),
    });
  }
  return chips;
}

function isDateInClosures(iso: string, closures: Closure[]): boolean {
  for (const c of closures) {
    if (typeof c.from === 'string' && typeof c.to === 'string' && c.from <= iso && iso <= c.to) {
      return true;
    }
  }
  return false;
}

// Returns true when the restaurant has no opening hours on the weekday this
// date falls on — the day is a recurring closure and must not be selectable.
// Backend reservations/service.py already returns an empty slot list for those
// days, but without this check the chip stays clickable and shows "no slots",
// which the staff/customer reads as a glitch.
export function isWeekdayClosed(iso: string, hours: WeeklyHours | undefined): boolean {
  if (!hours) return false;
  // Weekday of a bare calendar date: read in UTC so no timezone can shift it.
  const d = new Date(`${iso}T00:00:00Z`);
  // Date.getUTCDay() returns 0..6 starting at Sunday; DAY_KEYS starts at Monday.
  const dayKey = DAY_KEYS[(d.getUTCDay() + 6) % 7];
  const ranges = hours[dayKey];
  return Array.isArray(ranges) && ranges.length === 0;
}

function digitsOf(s: string): string {
  return s.replace(/\D/g, '');
}

function StampPanel({ children, className = '' }: { children: React.ReactNode; className?: string }) {
  return (
    <div
      className={`border-2 border-ink shadow-stamp bg-paper p-6 max-w-[640px] mx-auto my-12 text-center font-mono ${className}`}
    >
      {children}
    </div>
  );
}

export default function ReservationPage() {
  const { restaurantUuid = '' } = useParams<{ restaurantUuid: string }>();
  const { t, i18n } = useTranslation();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const draft = useAppSelector(selectReservation);
  const isAuthed = useAppSelector(selectIsAuthenticated);
  const currentUser = useAppSelector(selectCurrentUser);
  const { identity, config: restConfig } = useRestaurant(restaurantUuid);

  useEffect(() => {
    if (!isAuthed || !currentUser) return;
    const patch: Partial<typeof draft.customer> = {};
    if (!draft.customer.name && currentUser.name) patch.name = currentUser.name;
    if (!draft.customer.phone && currentUser.phone) patch.phone = currentUser.phone;
    if (!draft.customer.email && currentUser.email) patch.email = currentUser.email;
    if (Object.keys(patch).length) dispatch(setCustomer(patch));
  }, [isAuthed, currentUser, dispatch, draft.customer.name, draft.customer.phone, draft.customer.email]);
  const {
    data: availability,
    isLoading: isSlotsLoading,
    isError: isSlotsError,
    error: slotsError,
    refetch: refetchSlots,
  } = useGetAvailabilityQuery(
    { restaurantUuid, date: draft.date ?? '' },
    { skip: !restaurantUuid || !draft.date },
  );
  const [createReservation, { isLoading: isSubmitting }] = useCreateReservationMutation();
  // Cloudflare Turnstile (managed → invisible for most visitors). Only enforced
  // when a site key is configured; without it (dev/CI) the backend fails open.
  const recaptchaSiteKey = import.meta.env.VITE_RECAPTCHA_SITE_KEY ?? '';
  const [recaptchaToken, setRecaptchaToken] = useState<string | null>(null);
  const turnstileRef = useRef<TurnstileWidgetHandle>(null);
  const [touched, setTouched] = useState<{ name?: boolean; phone?: boolean; email?: boolean }>({});
  const [submitAttempted, setSubmitAttempted] = useState(false);
  const partySizeSectionRef = useRef<HTMLElement>(null);
  const dateSectionRef = useRef<HTMLElement>(null);
  const timeSectionRef = useRef<HTMLElement>(null);
  const chosenRef = useRef<HTMLParagraphElement>(null);
  const nameRef = useRef<HTMLInputElement>(null);
  const phoneRef = useRef<HTMLInputElement>(null);
  const emailRef = useRef<HTMLInputElement>(null);

  // SC-1081 — fuseau du restaurant (hub `timezone`, pays en repli) : jours,
  // delai minimal et `reserved_for` se lisent a son horloge, pas au navigateur.
  const tz = useMemo(
    () => restaurantTz({ timezone: identity?.timezone, country: identity?.country ?? 'FR' }),
    [identity?.timezone, identity?.country],
  );
  // DF-0371 — 1..N où N est le maximum réglé au back-office (SO-87) ; avant,
  // 1..8 en dur, et le réglage n'atteignait que la vitrine.
  const maxPartySize = normalizeReservationMaxPartySize(identity?.reservationMaxPartySize);
  const partySizes = useMemo(
    () => Array.from({ length: maxPartySize }, (_, i) => i + 1),
    [maxPartySize],
  );
  const now = useMemo(() => new Date(), []);
  const dateChips = useMemo(() => buildDateChips(ADVANCE_DAYS, now, tz), [now, tz]);
  const closedDates = useMemo(() => {
    const merged = [...(identity?.closures ?? []), ...(identity?.reservationClosures ?? [])];
    return merged;
  }, [identity?.closures, identity?.reservationClosures]);

  const slots = useMemo(() => {
    const all = availability?.slots ?? [];
    const date = draft.date;
    if (!date) return all;
    const cutoffMs = Date.now() + RESERVATION_MIN_LEAD_MS;
    return all.filter((slot) => {
      const iso = buildReservedFor(date, slot.time, tz);
      return new Date(iso).getTime() > cutoffMs;
    });
  }, [availability?.slots, draft.date, tz]);

  useEffect(() => {
    if (draft.time && !slots.some((s) => s.time === draft.time)) {
      dispatch(setTime(null));
    }
  }, [slots, draft.time, dispatch]);

  // DF-0423 — heure, puis minutes : au pas de 5 min le <select> faisait 105
  // lignes. L'heure active se deduit du creneau choisi ; `pickedHour` ne sert
  // qu'entre les deux touches.
  const services = useMemo(() => {
    const times = slots.map((s) => s.time);
    return groupSlots(times, times);
  }, [slots]);
  const [pickedHour, setPickedHour] = useState('');
  useEffect(() => setPickedHour(''), [draft.date]);
  const activeHour = draft.time ? draft.time.slice(0, 2) : pickedHour;
  const activeTimes =
    services.flatMap((s) => s.hours).find((h) => h.hour === activeHour)?.times ?? [];
  const pickHour = (hour: string, times: string[]) => {
    setPickedHour(hour);
    // DF-0427 — la touche sur l'heure choisit deja un creneau ; les minutes
    // affinent. Plus d'heure « allumee » sans rien de retenu.
    dispatch(setTime(slotForHour(times, draft.time)));
    // Sur un petit telephone (360x640) le recapitulatif nait sous la barre
    // collante du bas : on le ramene juste au-dessus, sans plus.
    requestAnimationFrame(() =>
      chosenRef.current?.scrollIntoView?.({ block: 'nearest', behavior: 'smooth' }),
    );
  };
  // DF-0427 — la ligne que le convive relit avant de valider.
  const chosenDate = draft.date
    ? new Intl.DateTimeFormat(i18n.language || 'fr', {
        weekday: 'long',
        day: 'numeric',
        month: 'long',
        timeZone: 'UTC',
      }).format(new Date(`${draft.date}T12:00:00Z`))
    : '';
  const chosenLabel =
    draft.date && draft.time
      ? t('reservation.slotChosen', {
          date: chosenDate.charAt(0).toUpperCase() + chosenDate.slice(1),
          time: draft.time,
        })
      : '';

  // Feature gate: acceptsReservations=false from config
  if (restConfig && restConfig.acceptsReservations === false) {
    return (
      <StampPanel>
        <p className="font-display text-xl mb-4">{t('reservation.notAccepted')}</p>
        <Link
          to={`/${restaurantUuid}`}
          className="inline-block bg-mint text-[var(--on-primary)] border-2 border-ink rounded-btn px-6 py-3 font-display shadow-stamp-sm"
        >
          {t('reservation.backToMenu')}
        </Link>
      </StampPanel>
    );
  }

  // "Site ouvert" shutter: it is per channel (SC-1434), so only a pause that
  // includes réservation closes this page.
  if (identity && isChannelPaused(identity, 'reservation')) {
    return (
      <StampPanel>
        <p className="font-display text-xl mb-4">{t('reservation.sitePaused')}</p>
        <Link
          to={`/${restaurantUuid}`}
          className="inline-block bg-mint text-[var(--on-primary)] border-2 border-ink rounded-btn px-6 py-3 font-display shadow-stamp-sm"
        >
          {t('reservation.backToMenu')}
        </Link>
      </StampPanel>
    );
  }

  // Feature gate: availability 404 means the feature is off at the backend level
  const slotsStatus = (slotsError as { status?: number } | undefined)?.status;
  if (draft.date && isSlotsError && slotsStatus === 404) {
    return (
      <StampPanel>
        <p className="font-display text-xl mb-4">{t('reservation.featureDisabled')}</p>
        <Link
          to={`/${restaurantUuid}`}
          className="inline-block bg-mint text-[var(--on-primary)] border-2 border-ink rounded-btn px-6 py-3 font-display shadow-stamp-sm"
        >
          {t('reservation.backToMenu')}
        </Link>
      </StampPanel>
    );
  }

  const phoneDigits = digitsOf(draft.customer.phone);
  const nameValid = draft.customer.name.trim().length > 0;
  const phoneValid = phoneDigits.length >= 8;
  const emailValid = draft.customer.email.includes('@') && draft.customer.email.includes('.');
  const ctaDisabled = isSubmitting;

  function scrollSectionIntoView(el: HTMLElement | null) {
    if (!el) return;
    el.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }

  function focusInput(input: HTMLInputElement | null) {
    if (!input) return;
    input.scrollIntoView({ behavior: 'smooth', block: 'center' });
    setTimeout(() => input.focus({ preventScroll: true }), 350);
  }

  function focusFirstInvalid(): boolean {
    if (!draft.partySize) { scrollSectionIntoView(partySizeSectionRef.current); return true; }
    if (!draft.date) { scrollSectionIntoView(dateSectionRef.current); return true; }
    if (!draft.time) { scrollSectionIntoView(timeSectionRef.current); return true; }
    if (!nameValid) { focusInput(nameRef.current); return true; }
    if (!phoneValid) { focusInput(phoneRef.current); return true; }
    if (!emailValid) { focusInput(emailRef.current); return true; }
    return false;
  }

  const onSubmit = async () => {
    setSubmitAttempted(true);
    setTouched({ name: true, phone: true, email: true });
    if (!draft.partySize || !draft.date || !draft.time || !nameValid || !phoneValid || !emailValid) {
      focusFirstInvalid();
      dispatch(showErrorToast(t('reservation.errorMissingFields')));
      return;
    }
    if (recaptchaSiteKey && !recaptchaToken) {
      // Managed Turnstile normally resolves a token on its own within ~1s; this
      // guards the rare race where the user submits before it is ready.
      dispatch(showErrorToast(t('reservation.errorGeneric')));
      return;
    }
    const reservedFor = buildReservedFor(draft.date, draft.time, tz);
    try {
      const result = await createReservation({
        restaurantUuid,
        customerName: draft.customer.name.trim(),
        customerPhone: draft.customer.phone.trim(),
        customerEmail: draft.customer.email.trim(),
        partySize: draft.partySize,
        reservedFor,
        notes: draft.note,
        language: i18n.language || 'fr',
        recaptchaToken: recaptchaToken ?? '',
      }).unwrap();
      dispatch(resetReservation());
      navigate(
        `/${restaurantUuid}/reservation/confirmation/${result.uuid}`,
        { state: { reservation: result } },
      );
    } catch (err) {
      // The Turnstile token is single-use: whether captcha passed (and a later
      // check failed) or captcha itself failed, a retry needs a fresh token.
      turnstileRef.current?.reset();
      setRecaptchaToken(null);
      const detail = (err as { data?: { detail?: string } })?.data?.detail;
      if (detail === 'captcha_failed') {
        dispatch(showErrorToast(t('reservation.errorGeneric')));
      } else if (detail === 'slot_full') {
        dispatch(setTime(null));
        dispatch(showErrorToast(t('reservation.errorSlotFull')));
        document.getElementById('reservation-step-3')?.scrollIntoView({ behavior: 'smooth' });
      } else if (detail === 'slot_too_soon') {
        dispatch(setTime(null));
        dispatch(showErrorToast(t('reservation.errorSlotTooSoon')));
        document.getElementById('reservation-step-3')?.scrollIntoView({ behavior: 'smooth' });
      } else if (detail === 'slot_not_in_template') {
        dispatch(setTime(null));
        dispatch(showErrorToast(t('reservation.errorSlotInvalid')));
      } else if (detail === 'reservation_date_closed') {
        dispatch(setTime(null));
        dispatch(showErrorToast(t('reservation.errorDateClosed')));
        document.getElementById('reservation-step-3')?.scrollIntoView({ behavior: 'smooth' });
      } else if (detail === 'too_many_reservations') {
        // SC-927 — the anti-spam cap, not a failure: say so, and say when it
        // lifts. "Échec, réessayez" here loops the guest until they give up.
        const wait = retryAfterSeconds(err);
        dispatch(showErrorToast(
          wait
            ? t('reservation.errorTooManyReservations', {
                when: formatRetryDelay(wait, i18n.language),
              })
            : t('reservation.errorTooManyReservationsNoDelay'),
        ));
      } else if ((err as { status?: number })?.status === 429) {
        // The per-IP throttle — same need, different cause (public wifi, or a
        // burst of retries), so the wording blames the device, not the guest.
        const wait = retryAfterSeconds(err);
        dispatch(showErrorToast(
          wait
            ? t('reservation.errorTooManyRequests', {
                when: formatRetryDelay(wait, i18n.language),
              })
            : t('reservation.errorTooManyRequestsNoDelay'),
        ));
      } else if ((err as { status?: number })?.status === 404) {
        dispatch(showErrorToast(t('reservation.featureDisabled')));
      } else {
        dispatch(showErrorToast(t('reservation.errorGeneric')));
      }
    }
  };

  // sticky CTA summary
  const summaryBits: string[] = [];
  if (draft.partySize) summaryBits.push(`👥 ${draft.partySize} pers`);
  if (draft.date) {
    const d = new Date(`${draft.date}T00:00:00`);
    summaryBits.push(`📅 ${WEEKDAYS_FR[d.getDay()]} ${pad(d.getDate())}`);
  }
  if (draft.time) summaryBits.push(`🕗 ${draft.time}`);

  return (
    <div className="max-w-[800px] mx-auto px-4 py-8 space-y-6">
      <h1 className="font-display text-3xl">{t('reservation.title')}</h1>

      {/* Step 1: Personnes */}
      <section
        ref={partySizeSectionRef}
        className={`border-2 bg-paper p-5 shadow-stamp ${
          submitAttempted && !draft.partySize ? 'border-sale-red' : 'border-ink'
        }`}
      >
        <h2 className="font-display text-xl mb-3">{t('reservation.step1Title')}</h2>
        {submitAttempted && !draft.partySize && (
          <p className="mb-2 font-mono text-sm text-sale-red">⚠ {t('reservation.requiredPartySize')}</p>
        )}
        <div className="flex flex-wrap gap-3">
          {partySizes.map((n) => (
            <button
              key={n}
              type="button"
              onClick={() => dispatch(setPartySize(n))}
              className={`w-12 h-12 rounded-full border-2 border-ink font-display ${
                draft.partySize === n ? 'bg-mint' : 'bg-paper hover:bg-cream'
              }`}
              aria-pressed={draft.partySize === n}
            >
              {n}
            </button>
          ))}
        </div>
        {identity?.phone && (
          <p className="mt-3 font-mono text-sm text-ink/70">
            <a
              href={`tel:${identity.phone.replace(/\s/g, '')}`}
              className="underline"
            >
              <Trans
                i18nKey="reservation.callForLargeGroups"
                values={{ phone: identity.phone, max: maxPartySize }}
                components={{ call: <strong className="font-bold text-sale-red" /> }}
              />
            </a>
          </p>
        )}
      </section>

      {/* Step 2: Date */}
      <section
        ref={dateSectionRef}
        className={`border-2 bg-paper p-5 shadow-stamp ${
          submitAttempted && !draft.date ? 'border-sale-red' : 'border-ink'
        }`}
      >
        <h2 className="font-display text-xl mb-3">{t('reservation.step2Title')}</h2>
        {submitAttempted && !draft.date && (
          <p className="mb-2 font-mono text-sm text-sale-red">⚠ {t('reservation.requiredDate')}</p>
        )}
        <div className="flex gap-2 overflow-x-auto snap-x pb-2">
          {dateChips.map((chip, idx) => {
            const selected = draft.date === chip.date;
            const isSpecial = idx <= 1;
            const top = idx === 0 ? t('reservation.today') : idx === 1 ? t('reservation.tomorrow') : chip.topLabel;
            const bottom = isSpecial ? '' : chip.bottomLabel;
            const closed =
              isDateInClosures(chip.date, closedDates) ||
              isWeekdayClosed(chip.date, identity?.reservationHours ?? identity?.hours);
            return (
              <button
                key={chip.date}
                type="button"
                onClick={() => dispatch(setDate(chip.date))}
                disabled={closed}
                aria-disabled={closed}
                aria-label={closed ? `${top} ${bottom} — ${t('reservation.chipClosedLabel')}` : undefined}
                className={`min-w-20 border-2 border-ink p-3 font-display text-center snap-start ${
                  closed
                    ? 'bg-ink/5 text-ink/40 line-through cursor-not-allowed'
                    : selected ? 'bg-mint' : 'bg-paper hover:bg-cream'
                }`}
                aria-pressed={selected}
              >
                <div className={isSpecial ? 'text-xs leading-tight break-words' : ''}>{top}</div>
                {bottom !== '' && <div className="text-sm">{bottom}</div>}
              </button>
            );
          })}
        </div>
      </section>

      {/* Step 3: Créneau */}
      <section
        id="reservation-step-3"
        ref={timeSectionRef}
        className={`border-2 bg-paper p-5 shadow-stamp ${
          submitAttempted && !draft.time ? 'border-sale-red' : 'border-ink'
        }`}
      >
        <h2 className="font-display text-xl mb-3">{t('reservation.step3Title')}</h2>
        {submitAttempted && !draft.time && (
          <p className="mb-2 font-mono text-sm text-sale-red">
            {/* DF-0427 — nommer ce qui manque : l'heure est touchee, pas les minutes. */}
            ⚠{' '}
            {activeTimes[0]
              ? t('reservation.requiredMinutes', { example: activeTimes[0] })
              : t('reservation.requiredTime')}
          </p>
        )}
        {!draft.date ? null : isSlotsLoading ? (
          <p className="text-ink/60 font-mono">{t('loading.label')}</p>
        ) : isSlotsError && slotsStatus !== 404 ? (
          <div>
            <p className="text-ink/60 font-mono mb-2">{t('reservation.errorAvailability')}</p>
            <button
              type="button"
              onClick={() => refetchSlots()}
              className="border-2 border-ink rounded-btn px-4 py-2 font-display text-sm"
            >
              {t('reservation.retrySlots')}
            </button>
          </div>
        ) : slots.length === 0 ? (
          <p className="text-ink/60 font-mono">{t('reservation.noSlotsAvailable')}</p>
        ) : (
          // Fullness check temporarily disabled — show every template slot
          // regardless of .available. The backend still rejects with
          // slot_full on submit if needed.
          <div className="flex flex-col gap-3">
            {/* DF-0427 — consigne visible : « 3. Creneau » seul ne disait pas
                qu'il y a une deuxieme touche. */}
            <p className="font-display text-base">{t('reservation.slotStepHour')}</p>
            {services.map((service) => (
              <div key={service.from}>
                {services.length > 1 && (
                  <p className="mb-1 font-mono text-xs uppercase tracking-wide text-ink/60">
                    {service.period === 'lunch'
                      ? t('reservation.serviceLunch')
                      : service.period === 'dinner'
                        ? t('reservation.serviceDinner')
                        : `${service.from} – ${service.to}`}
                  </p>
                )}
                <div className="flex flex-wrap gap-2">
                  {service.hours.map((h) => (
                    <button
                      key={h.hour}
                      type="button"
                      onClick={() => pickHour(h.hour, h.times)}
                      className={`min-w-14 h-12 px-2 border-2 border-ink rounded-btn font-display text-base ${
                        activeHour === h.hour ? 'bg-mint font-bold' : 'bg-paper hover:bg-cream'
                      }`}
                      aria-pressed={activeHour === h.hour}
                    >
                      {t('reservation.slotHour', { hour: Number(h.hour) })}
                    </button>
                  ))}
                </div>
                {activeTimes.length > 0 && service.hours.some((h) => h.hour === activeHour) && (
                  <div
                    role="group"
                    aria-labelledby="reservation-minutes-title"
                    className="mt-3 flex flex-wrap gap-2 border-2 border-solid border-ink/20 border-l-8 border-l-mint bg-cream p-3"
                  >
                    <p id="reservation-minutes-title" className="w-full font-display text-base">
                      {t('reservation.slotMinutesOf', { hour: Number(activeHour) })}
                    </p>
                    {activeTimes.map((time) => (
                      <button
                        key={time}
                        type="button"
                        onClick={() => dispatch(setTime(time))}
                        className={`min-w-16 h-12 px-2 border-2 border-ink rounded-btn font-mono text-base ${
                          draft.time === time ? 'bg-mint font-bold' : 'bg-paper hover:bg-cream'
                        }`}
                        aria-pressed={draft.time === time}
                      >
                        {draft.time === time && <span aria-hidden="true">✓ </span>}
                        {time}
                      </button>
                    ))}
                    {/* Sous les minutes, la ou le doigt vient de toucher : en bas de
                        section il tombait sous la barre collante du telephone. */}
                    <p
                      ref={chosenRef}
                      aria-hidden="true"
                      className="w-full mt-1 font-display text-lg scroll-mb-36"
                    >
                      {chosenLabel}
                    </p>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
        <p aria-live="polite" className="sr-only">
          {chosenLabel}
        </p>
      </section>

      {/* Step 4: Coordonnées */}
      <section className="border-2 border-ink bg-paper p-5 shadow-stamp">
        <h2 className="font-display text-xl mb-3">{t('reservation.step4Title')}</h2>
        <div className="grid sm:grid-cols-2 gap-3">
          <div>
            <input
              ref={nameRef}
              className={`border-2 p-2 font-mono w-full ${(touched.name || submitAttempted) && !nameValid ? 'border-sale-red' : 'border-ink'}`}
              placeholder={t('reservation.namePlaceholder')}
              value={draft.customer.name}
              onChange={(e) => dispatch(setCustomer({ name: e.target.value }))}
              onBlur={() => setTouched((p) => ({ ...p, name: true }))}
            />
            {(touched.name || submitAttempted) && !nameValid && (
              <p className="mt-1 font-mono text-xs text-sale-red">⚠ {t('reservation.invalidName')}</p>
            )}
          </div>
          <div>
            <input
              ref={phoneRef}
              className={`border-2 p-2 font-mono w-full ${(touched.phone || submitAttempted) && !phoneValid ? 'border-sale-red' : 'border-ink'}`}
              placeholder={t('reservation.phonePlaceholder')}
              value={draft.customer.phone}
              onChange={(e) => dispatch(setCustomer({ phone: e.target.value }))}
              onBlur={() => setTouched((p) => ({ ...p, phone: true }))}
            />
            {(touched.phone || submitAttempted) && !phoneValid && (
              <p className="mt-1 font-mono text-xs text-sale-red">⚠ {t('reservation.invalidPhone')}</p>
            )}
          </div>
          <div className="col-span-full">
            <input
              ref={emailRef}
              className={`border-2 p-2 font-mono w-full ${(touched.email || submitAttempted) && !emailValid ? 'border-sale-red' : 'border-ink'}`}
              placeholder={t('reservation.emailPlaceholder')}
              value={draft.customer.email}
              onChange={(e) => dispatch(setCustomer({ email: e.target.value }))}
              onBlur={() => setTouched((p) => ({ ...p, email: true }))}
            />
            {(touched.email || submitAttempted) && !emailValid && (
              <p className="mt-1 font-mono text-xs text-sale-red">⚠ {t('reservation.invalidEmail')}</p>
            )}
          </div>
        </div>
      </section>

      {/* Step 5: Note */}
      <section className="border-2 border-ink bg-paper p-5 shadow-stamp">
        <h2 className="font-display text-xl mb-3">{t('reservation.step5Title')}</h2>
        <textarea
          rows={3}
          maxLength={1000}
          className="border-2 border-ink p-2 font-mono w-full"
          placeholder={t('reservation.notePlaceholder')}
          value={draft.note}
          onChange={(e) => dispatch(setNote(e.target.value))}
        />
        <div className="font-mono text-xs text-ink/60 text-right">{draft.note.length}/1000</div>
      </section>

      {recaptchaSiteKey && (
        <div className="flex justify-center">
          <TurnstileWidget
            ref={turnstileRef}
            siteKey={recaptchaSiteKey}
            onSuccess={(token) => setRecaptchaToken(token)}
            onExpire={() => setRecaptchaToken(null)}
            onError={() => setRecaptchaToken(null)}
          />
        </div>
      )}

      {/* Sticky CTA */}
      <div className="sticky bottom-0 bg-paper border-2 border-ink p-4 shadow-stamp flex items-center justify-between gap-3">
        <div className="font-mono text-sm">
          {summaryBits.length === 0 ? ' ' : summaryBits.join(' · ')}
        </div>
        <button
          type="button"
          onClick={onSubmit}
          disabled={ctaDisabled}
          className="bg-mint text-[var(--on-primary)] border-2 border-ink rounded-btn px-6 py-3 font-display shadow-stamp-sm disabled:opacity-50"
        >
          {isSubmitting ? t('reservation.submitting') : t('reservation.submitCta')}
        </button>
      </div>
    </div>
  );
}
